/**
 * Lead-capture modal — email-only delivery.
 * First name + Email -> POST /api/leads (server emails the PDF) -> success state.
 */
import { useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LeadGate({ open, onClose }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [deliveredTo, setDeliveredTo] = useState("");
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset on every open
  useEffect(() => {
    if (open) {
      setError("");
      setDone(false);
      setSubmitting(false);
      setDeliveredTo("");
    }
  }, [open]);

  if (!open) return null;

  const validate = () => {
    if (!firstName.trim()) return "Please enter your first name.";
    if (!EMAIL_RE.test(email.trim())) return "Please enter a valid email.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim().toLowerCase(),
          source: "decision-filter",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.detail || data.message || "Something went wrong. Please try again."
        );
      }
      setDeliveredTo(data.delivered_to || email.trim().toLowerCase());
      setDone(true);
    } catch (err) {
      setError(typeof err.message === "string" ? err.message : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="lg__overlay"
      data-testid="lead-gate-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="lg__dialog"
        role="dialog"
        aria-labelledby="lg-title"
        data-testid="lead-gate-dialog"
      >
        <button
          type="button"
          className="lg__close"
          onClick={onClose}
          aria-label="Close"
          data-testid="lead-gate-close"
        >
          ×
        </button>

        {!done ? (
          <>
            <span className="lg__eyebrow">FREE DOWNLOAD</span>
            <span className="lg__rule" />
            <h3 id="lg-title" className="lg__title" data-testid="lead-gate-title">
              Where should we send it?
            </h3>
            <p className="lg__deck">
              Drop your first name + email and the 7-page Decision Filter
              lands in your inbox in seconds. One email a week, frame-style.
              Unsubscribe anytime.
            </p>

            <form className="lg__form" onSubmit={submit} noValidate>
              <label className="lg__field">
                <span className="lg__label">First name</span>
                <input
                  ref={firstFieldRef}
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Alex"
                  maxLength={80}
                  disabled={submitting}
                  data-testid="lead-gate-first-name"
                />
              </label>

              <label className="lg__field">
                <span className="lg__label">Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@domain.com"
                  disabled={submitting}
                  data-testid="lead-gate-email"
                />
              </label>

              {error && (
                <p className="lg__error" data-testid="lead-gate-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="lg__cta"
                disabled={submitting}
                data-testid="lead-gate-submit"
              >
                {submitting ? "Sending…" : "Email me the checklist  →"}
              </button>

              <span className="lg__meta">
                PDF · 7 pages · 124 KB · 48 fillable fields
              </span>
            </form>
          </>
        ) : (
          <div className="lg__success" data-testid="lead-gate-success">
            <span className="lg__eyebrow">SENT</span>
            <span className="lg__rule" />
            <div className="lg__check" aria-hidden="true">
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <circle cx="22" cy="22" r="21" stroke="#E15A2C" strokeWidth="2" />
                <path
                  d="M13 22.5l6 6L31 16"
                  stroke="#E15A2C"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 className="lg__title">Check your inbox.</h3>
            <p className="lg__deck">
              Your Decision Filter is on its way to{" "}
              <strong style={{ color: "#1a1a1a" }}>{deliveredTo}</strong>.
              Give it a minute. If it's hiding, peek in spam.
            </p>
            <button
              type="button"
              className="lg__cta lg__cta--ghost"
              onClick={onClose}
              data-testid="lead-gate-done"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
